import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ImageCropper = ({ imageFile, onCropComplete, onCancel, onCropCancel }) => {
    const handleCancel = onCropCancel || onCancel;
    const [imageSrc, setImageSrc] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0, radius: 100 });
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [containerSize, setContainerSize] = useState({ width: 500, height: 500 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [initialCrop, setInitialCrop] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [imageDrawn, setImageDrawn] = useState(false);
    const croppedFileRef = useRef(null);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    // Refs para que los listeners táctiles (passive: false) lean siempre el estado actual
    const cropRef = useRef(crop);
    const dragStartRef = useRef(dragStart);
    const initialCropRef = useRef(initialCrop);
    const containerSizeRef = useRef(containerSize);
    const isDraggingRef = useRef(false);
    const isResizingRef = useRef(false);
    cropRef.current = crop;
    dragStartRef.current = dragStart;
    initialCropRef.current = initialCrop;
    containerSizeRef.current = containerSize;
    isDraggingRef.current = isDragging;
    isResizingRef.current = isResizing;

    // Cargar la imagen cuando cambie el archivo
    useEffect(() => {
        if (!imageFile) return;
        setLoadError(null);
        setImageDrawn(false);
        const reader = new FileReader();
        reader.onerror = () => {
            setLoadError('No se pudo leer el archivo. Prueba con otra imagen.');
        };
        reader.onload = (e) => {
            const dataUrl = e.target?.result;
            if (!dataUrl) {
                setLoadError('No se pudo cargar la imagen.');
                return;
            }
            setImageSrc(dataUrl);
            const img = new Image();
            img.onerror = () => {
                setLoadError('No se pudo cargar la imagen para recortar. Prueba con otro formato (JPG/PNG).');
            };
            img.onload = () => {
                setImageSize({ width: img.width, height: img.height });
                // Limitar tamaño al viewport para que el modal no tenga scroll (cabecera + instrucción + canvas + botones ~260px)
                const reservedHeight = typeof window !== 'undefined' ? Math.min(280, window.innerHeight * 0.35) : 280;
                const maxHeight = typeof window !== 'undefined' ? Math.min(700, window.innerHeight - reservedHeight) : 700;
                const maxWidth = typeof window !== 'undefined' ? Math.min(700, window.innerWidth - 48) : 700;
                let displayWidth = img.width;
                let displayHeight = img.height;
                if (displayWidth > maxWidth) {
                    displayHeight = (displayHeight * maxWidth) / displayWidth;
                    displayWidth = maxWidth;
                }
                if (displayHeight > maxHeight) {
                    displayWidth = (displayWidth * maxHeight) / displayHeight;
                    displayHeight = maxHeight;
                }
                setContainerSize({ width: displayWidth, height: displayHeight });
                const cropRadius = Math.min(displayWidth, displayHeight) * 0.3;
                setCrop({
                    x: displayWidth / 2,
                    y: displayHeight / 2,
                    radius: cropRadius
                });
            };
            img.src = dataUrl;
        };
        reader.readAsDataURL(imageFile);
    }, [imageFile]);

    // Dibujar la imagen y el área de recorte circular
    useEffect(() => {
        if (!imageSrc || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onerror = () => {
            setLoadError('No se pudo cargar la imagen para recortar. Prueba con otro formato (JPG/PNG).');
        };
        
        img.onload = () => {
            if (!canvasRef.current || !ctx) return;
            const w = Math.max(1, containerSize.width);
            const h = Math.max(1, containerSize.height);
            canvas.width = w;
            canvas.height = h;
            ctx.clearRect(0, 0, w, h);
            
            // Dibujar la imagen
            ctx.drawImage(img, 0, 0, w, h);
            
            // Dibujar overlay oscuro
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Crear un path para el círculo de recorte (hacer transparente)
            ctx.globalCompositeOperation = 'destination-out';
            ctx.beginPath();
            ctx.arc(crop.x, crop.y, crop.radius, 0, 2 * Math.PI);
            ctx.fill();
            
            // Restaurar modo de composición
            ctx.globalCompositeOperation = 'source-over';
            
            // Dibujar borde del círculo de recorte
            ctx.strokeStyle = '#1f94c2';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(crop.x, crop.y, crop.radius, 0, 2 * Math.PI);
            ctx.stroke();
            
            // Dibujar círculo guía interno
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(crop.x, crop.y, crop.radius - 2, 0, 2 * Math.PI);
            ctx.stroke();
            
            // Dibujar handles de redimensionamiento (4 puntos alrededor del círculo)
            const handleSize = 10;
            ctx.fillStyle = '#1f94c2';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            
            // Handle superior
            ctx.beginPath();
            ctx.arc(crop.x, crop.y - crop.radius, handleSize / 2, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            
            // Handle inferior
            ctx.beginPath();
            ctx.arc(crop.x, crop.y + crop.radius, handleSize / 2, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            
            // Handle izquierdo
            ctx.beginPath();
            ctx.arc(crop.x - crop.radius, crop.y, handleSize / 2, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            
            // Handle derecho
            ctx.beginPath();
            ctx.arc(crop.x + crop.radius, crop.y, handleSize / 2, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
            
            // Dibujar cruz en el centro
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(crop.x - 10, crop.y);
            ctx.lineTo(crop.x + 10, crop.y);
            ctx.moveTo(crop.x, crop.y - 10);
            ctx.lineTo(crop.x, crop.y + 10);
            ctx.stroke();
            setImageDrawn(true);
        };
        
        img.src = imageSrc;
    }, [imageSrc, crop, containerSize]);

    // Tolerancia mayor en móvil para dedos (detectar si es touch con ancho de pantalla)
    const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    const resizeHandleSize = isTouchDevice ? 28 : 15;

    // Detectar si el click es en un handle de redimensionamiento
    const getResizeHandle = (x, y) => {
        const handleSize = resizeHandleSize;
        const handles = [
            { name: 'top', x: crop.x, y: crop.y - crop.radius },
            { name: 'bottom', x: crop.x, y: crop.y + crop.radius },
            { name: 'left', x: crop.x - crop.radius, y: crop.y },
            { name: 'right', x: crop.x + crop.radius, y: crop.y },
        ];
        
        for (const handle of handles) {
            const distance = Math.sqrt(Math.pow(x - handle.x, 2) + Math.pow(y - handle.y, 2));
            if (distance < handleSize) {
                return handle.name;
            }
        }
        return null;
    };

    // Detectar si el click es dentro del círculo de recorte
    const isInsideCrop = (x, y) => {
        const distance = Math.sqrt(Math.pow(x - crop.x, 2) + Math.pow(y - crop.y, 2));
        const margin = isTouchDevice ? 18 : 5; // Mayor tolerancia en móvil para iniciar arrastre
        return distance <= (crop.radius + margin);
    };

    // Convierte coordenadas de pantalla (clientX/clientY) a coordenadas del canvas.
    // Necesario en móvil cuando el canvas se muestra escalado (ej. maxWidth: 100%).
    const getCanvasCoordinates = (clientX, clientY) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    // Función helper para obtener coordenadas de mouse o touch (en espacio del canvas)
    const getCoordinates = (e) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        let clientX, clientY;
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else if (e.changedTouches && e.changedTouches.length > 0) {
            clientX = e.changedTouches[0].clientX;
            clientY = e.changedTouches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }
        return getCanvasCoordinates(clientX, clientY);
    };

    const handleStart = (e) => {
        // Prevenir scroll y otros comportamientos por defecto
        if (e.cancelable) {
            e.preventDefault();
        }
        
        const { x, y } = getCoordinates(e);
        
        // Verificar primero si es un handle de redimensionamiento
        const handle = getResizeHandle(x, y);
        if (handle) {
            setIsResizing(true);
            setInitialCrop({ ...crop });
        } else if (isInsideCrop(x, y)) {
            // Si está dentro del círculo, activar arrastre
            setIsDragging(true);
            setDragStart({ x: x - crop.x, y: y - crop.y });
        }
    };
    
    const handleMove = (e) => {
        if (!canvasRef.current) return;
        
        // Prevenir scroll solo si estamos arrastrando o redimensionando
        if ((isDragging || isResizing) && e.cancelable) {
            e.preventDefault();
        }
        
        const { x, y } = getCoordinates(e);
        
        if (isResizing && initialCrop) {
            // Calcular la distancia desde el centro del círculo hasta el punto actual
            const distance = Math.sqrt(Math.pow(x - initialCrop.x, 2) + Math.pow(y - initialCrop.y, 2));
            const maxRadius = Math.min(
                initialCrop.x,
                initialCrop.y,
                containerSize.width - initialCrop.x,
                containerSize.height - initialCrop.y
            );
            
            let newCrop = { ...initialCrop };
            newCrop.radius = Math.max(30, Math.min(distance, maxRadius));
            
            setCrop(newCrop);
        } else if (isDragging) {
            // Mover el círculo
            const newX = Math.max(crop.radius, Math.min(x - dragStart.x, containerSize.width - crop.radius));
            const newY = Math.max(crop.radius, Math.min(y - dragStart.y, containerSize.height - crop.radius));
            setCrop({ ...crop, x: newX, y: newY });
        }
    };
    
    const handleEnd = (e) => {
        if (e.cancelable) {
            e.preventDefault();
        }
        setIsDragging(false);
        setIsResizing(false);
        setInitialCrop(null);
    };
    
    // Listeners táctiles en el canvas con { passive: false } para poder preventDefault() en móviles
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !imageDrawn) return;

        const handleSize = isTouchDevice ? 28 : 15;
        const getHandle = (x, y) => {
            const c = cropRef.current;
            const handles = [
                { name: 'top', x: c.x, y: c.y - c.radius },
                { name: 'bottom', x: c.x, y: c.y + c.radius },
                { name: 'left', x: c.x - c.radius, y: c.y },
                { name: 'right', x: c.x + c.radius, y: c.y },
            ];
            for (const h of handles) {
                if (Math.sqrt((x - h.x) ** 2 + (y - h.y) ** 2) < handleSize) return h.name;
            }
            return null;
        };
        const touchCropMargin = isTouchDevice ? 18 : 5;
        const insideCrop = (x, y) => {
            const c = cropRef.current;
            return Math.sqrt((x - c.x) ** 2 + (y - c.y) ** 2) <= c.radius + touchCropMargin;
        };

        const getTouchCanvasCoords = (clientX, clientY) => {
            if (!canvasRef.current) return { x: 0, y: 0 };
            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            return {
                x: (clientX - rect.left) * scaleX,
                y: (clientY - rect.top) * scaleY
            };
        };

        const onTouchStart = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!canvasRef.current) return;
            const touch = e.touches[0];
            const { x, y } = getTouchCanvasCoords(touch.clientX, touch.clientY);
            const c = cropRef.current;
            if (getHandle(x, y)) {
                isResizingRef.current = true;
                isDraggingRef.current = false;
                initialCropRef.current = { ...c };
                dragStartRef.current = { x: 0, y: 0 };
                setIsResizing(true);
                setInitialCrop({ ...c });
            } else if (insideCrop(x, y)) {
                isDraggingRef.current = true;
                isResizingRef.current = false;
                const ds = { x: x - c.x, y: y - c.y };
                dragStartRef.current = ds;
                initialCropRef.current = null;
                setIsDragging(true);
                setDragStart(ds);
            }
        };

        const onTouchMove = (e) => {
            if (isDraggingRef.current || isResizingRef.current) {
                e.preventDefault();
            }
            e.stopPropagation();
            if (!canvasRef.current) return;
            const touch = e.touches[0];
            const { x, y } = getTouchCanvasCoords(touch.clientX, touch.clientY);
            const c = cropRef.current;
            const init = initialCropRef.current;
            const sz = containerSizeRef.current;
            const ds = dragStartRef.current;
            if (isResizingRef.current && init) {
                const distance = Math.sqrt((x - init.x) ** 2 + (y - init.y) ** 2);
                const maxRadius = Math.min(init.x, init.y, sz.width - init.x, sz.height - init.y);
                const radius = Math.max(30, Math.min(distance, maxRadius));
                setCrop({ ...init, radius });
            } else if (isDraggingRef.current) {
                const newX = Math.max(c.radius, Math.min(x - ds.x, sz.width - c.radius));
                const newY = Math.max(c.radius, Math.min(y - ds.y, sz.height - c.radius));
                setCrop(prev => ({ ...prev, x: newX, y: newY }));
            }
        };

        const onTouchEnd = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isDraggingRef.current = false;
            isResizingRef.current = false;
            initialCropRef.current = null;
            dragStartRef.current = { x: 0, y: 0 };
            setIsDragging(false);
            setIsResizing(false);
            setInitialCrop(null);
        };

        canvas.addEventListener('touchstart', onTouchStart, { passive: false });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
        canvas.addEventListener('touchend', onTouchEnd, { passive: false });
        canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
        return () => {
            canvas.removeEventListener('touchstart', onTouchStart);
            canvas.removeEventListener('touchmove', onTouchMove);
            canvas.removeEventListener('touchend', onTouchEnd);
            canvas.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [imageDrawn]);

    // Agregar listeners globales para eventos táctiles en móvil
    useEffect(() => {
        if (!isDragging && !isResizing) return;

        const handleGlobalMove = (e) => {
            if (!canvasRef.current) return;
            e.preventDefault(); // Siempre prevenir en touch
            
            let x, y;
            if (e.touches && e.touches.length > 0) {
                const coords = getCanvasCoordinates(e.touches[0].clientX, e.touches[0].clientY);
                x = coords.x;
                y = coords.y;
            } else {
                return;
            }
            
            // Asegurar que las coordenadas estén dentro del canvas
            x = Math.max(0, Math.min(x, containerSize.width));
            y = Math.max(0, Math.min(y, containerSize.height));
            
            if (isResizing && initialCrop) {
                const distance = Math.sqrt(Math.pow(x - initialCrop.x, 2) + Math.pow(y - initialCrop.y, 2));
                const maxRadius = Math.min(
                    initialCrop.x,
                    initialCrop.y,
                    containerSize.width - initialCrop.x,
                    containerSize.height - initialCrop.y
                );
                
                let newCrop = { ...initialCrop };
                newCrop.radius = Math.max(30, Math.min(distance, maxRadius));
                setCrop(newCrop);
            } else if (isDragging) {
                const newX = Math.max(crop.radius, Math.min(x - dragStart.x, containerSize.width - crop.radius));
                const newY = Math.max(crop.radius, Math.min(y - dragStart.y, containerSize.height - crop.radius));
                setCrop(prev => ({ ...prev, x: newX, y: newY }));
            }
        };

        const handleGlobalEnd = (e) => {
            e.preventDefault();
            setIsDragging(false);
            setIsResizing(false);
            setInitialCrop(null);
        };

        // Agregar listeners al documento para capturar eventos fuera del canvas
        document.addEventListener('touchmove', handleGlobalMove, { passive: false });
        document.addEventListener('touchend', handleGlobalEnd, { passive: false });
        document.addEventListener('touchcancel', handleGlobalEnd, { passive: false });

        return () => {
            document.removeEventListener('touchmove', handleGlobalMove);
            document.removeEventListener('touchend', handleGlobalEnd);
            document.removeEventListener('touchcancel', handleGlobalEnd);
        };
    }, [isDragging, isResizing, initialCrop, dragStart, crop.radius, containerSize]);

    const handleCrop = () => {
        if (!imageSrc || !canvasRef.current) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
            // Calcular la relación entre el tamaño de visualización y el tamaño real
            const scaleX = imageSize.width / containerSize.width;
            const scaleY = imageSize.height / containerSize.height;
            
            // Calcular las coordenadas y tamaño reales del recorte
            const realCenterX = crop.x * scaleX;
            const realCenterY = crop.y * scaleY;
            const realRadius = crop.radius * Math.max(scaleX, scaleY);
            
            // Tamaño del canvas de salida (círculo recortado)
            const outputSize = Math.ceil(realRadius * 2);
            canvas.width = outputSize;
            canvas.height = outputSize;
            
            const centerX = outputSize / 2;
            const centerY = outputSize / 2;
            
            // Dibujar la imagen recortada
            ctx.save();
            ctx.beginPath();
            ctx.arc(centerX, centerY, realRadius, 0, 2 * Math.PI);
            ctx.clip();
            
            // Dibujar la parte recortada de la imagen
            ctx.drawImage(
                img,
                realCenterX - realRadius, realCenterY - realRadius,
                realRadius * 2, realRadius * 2,
                0, 0,
                outputSize, outputSize
            );
            
            ctx.restore();
            
            // Convertir a blob y luego a File
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error('Error: No se pudo crear el blob de la imagen recortada');
                    return;
                }
                const croppedFile = new File([blob], imageFile.name || 'foto_perfil.png', {
                    type: imageFile.type || 'image/png',
                    lastModified: Date.now()
                });
                croppedFileRef.current = croppedFile;
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                setShowPreview(true);
            }, imageFile.type || 'image/png', 0.95);
        };
        
        img.src = imageSrc;
    };

    const handleConfirmPhoto = () => {
        if (croppedFileRef.current && onCropComplete) {
            onCropComplete(croppedFileRef.current);
        }
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setShowPreview(false);
        setPreviewUrl(null);
        croppedFileRef.current = null;
    };

    const handleBackToCrop = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setShowPreview(false);
        setPreviewUrl(null);
        croppedFileRef.current = null;
    };

    // Renderizar siempre en document.body para evitar z-index/overflow en producción (ej. AWS)
    const renderModal = (content) => {
        if (typeof document === 'undefined') return content;
        return createPortal(content, document.body);
    };

    if (loadError) {
        return renderModal(
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]" style={{ position: 'fixed' }}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl text-center">
                    <h3 className="text-xl font-bold text-[#1f94c2ff] mb-2">Error al cargar la imagen</h3>
                    <p className="text-sm text-gray-600 mb-4">{loadError}</p>
                    <button
                        onClick={handleCancel}
                        className="px-5 py-2.5 bg-[#1f94c2ff] text-white rounded-lg hover:bg-[#1a7a9d] transition font-medium"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        );
    }

    if (!imageSrc) {
        return renderModal(
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]" style={{ position: 'fixed' }}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl text-center">
                    <p className="text-gray-600">Cargando imagen...</p>
                </div>
            </div>
        );
    }

    // Vista previa: cómo se verá la foto de perfil (circular como en la app)
    if (showPreview && previewUrl) {
        return renderModal(
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] overflow-hidden" style={{ position: 'fixed' }}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[100vh] overflow-hidden flex flex-col">
                    <h3 className="text-xl font-bold text-[#1f94c2ff] mb-2 text-center">
                        Así se verá tu foto de perfil
                    </h3>
                    <p className="text-sm text-gray-600 mb-6 text-center">
                        Vista previa en el mismo formato que verás en tu perfil.
                    </p>
                    <div className="flex justify-center mb-6">
                        <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-[#1f94c2ff] bg-gray-100 shadow-lg flex items-center justify-center">
                            <img
                                src={previewUrl}
                                alt="Vista previa foto de perfil"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <button
                            onClick={handleBackToCrop}
                            className="px-5 py-2.5 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition font-medium"
                        >
                            Volver a recortar
                        </button>
                        <button
                            onClick={handleConfirmPhoto}
                            className="px-5 py-2.5 bg-[#1f94c2ff] text-white rounded-lg hover:bg-[#1a7a9d] transition font-medium"
                        >
                            Usar esta foto
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return renderModal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] overflow-hidden" style={{ position: 'fixed' }}>
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-4xl w-full mx-4 flex flex-col max-h-[100vh] overflow-hidden">
                <h3 className="text-xl font-bold text-[#1f94c2ff] mb-1 text-center shrink-0">
                    Recortar Foto de Perfil
                </h3>
                <p className="text-sm text-gray-600 mb-3 text-center shrink-0">
                    Arrastra el círculo para moverlo o los puntos para redimensionarlo.
                </p>
                
                {/* Canvas de recorte: área flexible sin scroll */}
                <div 
                    ref={containerRef}
                    className="flex justify-center items-center min-h-0 flex-1 mb-3 bg-gray-100 rounded-lg p-2 sm:p-4 touch-none select-none overflow-hidden"
                    style={{ position: 'relative' }}
                >
                    {!imageDrawn && (
                        <div
                            className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-200 z-10"
                            style={{ minHeight: Math.max(160, containerSize.height) }}
                        >
                            <p className="text-gray-600 font-medium">Cargando imagen...</p>
                        </div>
                    )}
                    <canvas
                        ref={canvasRef}
                        width={containerSize.width}
                        height={containerSize.height}
                        onMouseDown={handleStart}
                        onMouseMove={handleMove}
                        onMouseUp={handleEnd}
                        onMouseLeave={handleEnd}
                        style={{ 
                            cursor: isDragging ? 'grabbing' : (isResizing ? 'nwse-resize' : 'grab'),
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            touchAction: 'none', // Prevenir gestos del navegador en móvil
                            userSelect: 'none', // Prevenir selección de texto
                            WebkitUserSelect: 'none',
                            msUserSelect: 'none',
                            WebkitTouchCallout: 'none' // Prevenir menú contextual en iOS
                        }}
                    />
                </div>
                
                {/* Botones de acción */}
                <div className="flex justify-center gap-4 shrink-0">
                    <button
                        onClick={handleCancel}
                        className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition rounded-md"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleCrop}
                        className="px-6 py-2 bg-[#1f94c2ff] text-white rounded hover:bg-[#1a7a9d] transition rounded-md"
                    >
                        Aplicar Recorte
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropper;
